import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { MarineConcession, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const CONCESSION_ACTIVITIES = [
  'SEAWEED_FARMING',
  'AQUACULTURE_PARK',
  'MARINE_RENEWABLE_ENERGY',
  'MARINE_ECOTOURISM',
  'SAND_EXTRACTION_MARINE',
  'SUBMARINE_CABLE_PIPELINE',
  'MARINA_JETTY',
  'OTHER',
] as const;

/** M34 Blue Economy — marine concessions: GeoServe + MSP-zone site check, EIA referral, zone-conflict override, SeyPay fee, DocVault certificate. */
export function MarineConcessions() {
  const { persona } = useAuth();
  const isOfficer = persona ? persona.roles.includes('BLUE_ECON_OFFICER') : false;
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m34/marine-concessions?${params.toString()}`;
  }, [status, appliedQ]);
  const listQ = useApiQuery<Page<MarineConcession>>(path);

  return (
    <div>
      <PageHeader
        title="Marine Concessions"
        subtitle="Marine-area lease/concession awards — GeoServe + MSP zone site check, EIA referral (M34)."
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New concession application
          </button>
        }
      />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAppliedQ(q);
          }}
        >
          <input className="field-input !w-56" placeholder="Ref, site name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['APPLIED', 'SITE_CHECK', 'EIA_REFERRED', 'FEE_PENDING', 'ACTIVE', 'REJECTED', 'SUSPENDED', 'REVOKED'].map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
          <button type="submit" className="btn-secondary">
            Search
          </button>
        </form>
      </div>
      {!isOfficer ? <RoleNotice roles={['BLUE_ECON_OFFICER']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No marine concessions match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Site</th>
                <th className="table-head">Activity</th>
                <th className="table-head">Area (ha)</th>
                <th className="table-head">Zone conflict</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((c) => (
                <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(c.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{c.refNo}</td>
                  <td className="table-cell">{c.siteName}</td>
                  <td className="table-cell text-xs">{c.activity.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs">{c.areaHectares}</td>
                  <td className="table-cell text-xs">{c.zoneConflict ? <StatusBadge status="WARN" /> : '—'}</td>
                  <td className="table-cell">
                    <StatusBadge status={c.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <ConcessionDetailModal
          id={selectedId}
          isOfficer={isOfficer}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
      {showCreate ? (
        <NewConcessionModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ConcessionDetailModal({ id, isOfficer, onClose, onChanged }: { id: string; isOfficer: boolean; onClose: () => void; onChanged: () => void }) {
  const cq = useApiQuery<MarineConcession>(`/api/v1/m34/marine-concessions/${id}`);
  const c = cq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAction, setShowAction] = useState<'eia' | 'approve' | 'reject' | 'suspend' | 'revoke' | 'reinstate' | null>(null);
  const [note, setNote] = useState('');
  const [overrideJustification, setOverrideJustification] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      cq.reload();
      onChanged();
      setShowAction(null);
      setNote('');
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const confirm = () =>
    call(() => {
      if (showAction === 'eia') return api.post(`/api/v1/m34/marine-concessions/${id}/eia-clearance`, { note });
      if (showAction === 'approve')
        return api.post(`/api/v1/m34/marine-concessions/${id}/review`, {
          approve: true,
          reviewNotes: note || undefined,
          overrideJustification: c?.zoneConflict ? overrideJustification : undefined,
        });
      if (showAction === 'reject') return api.post(`/api/v1/m34/marine-concessions/${id}/review`, { approve: false, rejectionReason: note });
      if (showAction === 'reinstate') return api.post(`/api/v1/m34/marine-concessions/${id}/reinstate`);
      if (showAction === 'suspend') return api.post(`/api/v1/m34/marine-concessions/${id}/suspend`, { reason: note });
      return api.post(`/api/v1/m34/marine-concessions/${id}/revoke`, { reason: note });
    });

  return (
    <Modal title={c ? `${c.refNo} — ${c.siteName}` : 'Marine concession'} onClose={onClose} wide>
      {cq.error ? <ErrorNote message={cq.error} /> : null}
      {cq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {c ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={c.state} />} />
            <Detail label="Applicant" value={`${c.applicantType}:${c.applicantId}`} mono />
            <Detail label="Activity" value={c.activity.replaceAll('_', ' ')} />
            <Detail label="Area" value={`${c.areaHectares} ha`} />
            <Detail label="Term" value={`${c.proposedTermYears} years`} />
            <Detail label="District" value={c.district ?? '—'} />
            <Detail label="MSP zone conflict" value={c.zoneConflict ? 'Yes' : 'No'} />
            <Detail label="EIA required" value={c.eiaRequired ? 'Yes' : 'No'} />
            <Detail label="Fee" value={scr(c.feeAmountScr)} />
            <Detail label="Issued" value={formatDateTime(c.issuedAt)} />
            <Detail label="Valid" value={`${formatDate(c.validFrom)} → ${formatDate(c.validTo)}`} />
            <Detail label="Certificate serial" value={c.concessionSerial ?? '—'} mono />
          </div>
          <Detail label="Description" value={c.description} />
          {c.zoneConflict ? <ErrorNote message="Site falls in an MSP zone whose designation is not compatible with this activity — approval requires a documented override justification." /> : null}
          {c.rejectionReason ? <ErrorNote message={`Rejected: ${c.rejectionReason}`} /> : null}
          {c.suspendedReason ? <ErrorNote message={`Suspended: ${c.suspendedReason}`} /> : null}
          {c.revokedReason ? <ErrorNote message={`Revoked: ${c.revokedReason}`} /> : null}
          {c.eiaClearanceNote ? <Detail label="EIA clearance note" value={c.eiaClearanceNote} /> : null}

          {isOfficer ? (
            <div className="border-t border-slate-100 pt-4">
              {!showAction ? (
                <div className="flex flex-wrap gap-2">
                  {c.state === 'EIA_REFERRED' ? (
                    <button className="btn-success" disabled={busy} onClick={() => setShowAction('eia')}>
                      Record M31 EIA clearance
                    </button>
                  ) : null}
                  {c.state === 'SITE_CHECK' ? (
                    <>
                      <button className="btn-success" disabled={busy} onClick={() => setShowAction('approve')}>
                        Approve (open fee intent)
                      </button>
                      <button className="btn-danger" disabled={busy} onClick={() => setShowAction('reject')}>
                        Reject
                      </button>
                    </>
                  ) : null}
                  {c.state === 'ACTIVE' ? (
                    <button className="btn-secondary" disabled={busy} onClick={() => setShowAction('suspend')}>
                      Suspend
                    </button>
                  ) : null}
                  {c.state === 'SUSPENDED' ? (
                    <button className="btn-success" disabled={busy} onClick={() => setShowAction('reinstate')}>
                      Reinstate
                    </button>
                  ) : null}
                  {c.state === 'ACTIVE' || c.state === 'SUSPENDED' ? (
                    <button className="btn-danger" disabled={busy} onClick={() => setShowAction('revoke')}>
                      Revoke
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  {showAction === 'approve' && c.zoneConflict ? (
                    <Field label="Zone-override justification (required — this site conflicts with its MSP designation)">
                      <textarea className="field-input" rows={2} value={overrideJustification} onChange={(e) => setOverrideJustification(e.target.value)} />
                    </Field>
                  ) : null}
                  {showAction === 'reinstate' ? null : (
                    <Field label={showAction === 'approve' || showAction === 'eia' ? 'Note' : 'Reason'}>
                      <textarea className="field-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
                    </Field>
                  )}
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowAction(null)}>
                      Cancel
                    </button>
                    <button
                      className={showAction === 'approve' || showAction === 'eia' || showAction === 'reinstate' ? 'btn-success' : 'btn-danger'}
                      disabled={busy || (showAction === 'approve' && c.zoneConflict && !overrideJustification)}
                      onClick={confirm}
                    >
                      {busy ? 'Submitting…' : 'Confirm'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewConcessionModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [activity, setActivity] = useState<(typeof CONCESSION_ACTIVITIES)[number]>('AQUACULTURE_PARK');
  const [siteName, setSiteName] = useState('');
  const [description, setDescription] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [areaHectares, setAreaHectares] = useState('');
  const [district, setDistrict] = useState('');
  const [proposedTermYears, setProposedTermYears] = useState('10');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m34/marine-concessions', {
        activity,
        siteName,
        description,
        areaGeometry: { type: 'Point', coordinates: [Number.parseFloat(lng), Number.parseFloat(lat)] },
        areaHectares: Number.parseFloat(areaHectares),
        district: district || undefined,
        proposedTermYears: Number.parseInt(proposedTermYears, 10),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New marine concession application" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Activity">
            <select className="field-input" value={activity} onChange={(e) => setActivity(e.target.value as (typeof CONCESSION_ACTIVITIES)[number])}>
              {CONCESSION_ACTIVITIES.map((a) => (
                <option key={a} value={a}>
                  {a.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Proposed term (years)">
            <input className="field-input" type="number" value={proposedTermYears} onChange={(e) => setProposedTermYears(e.target.value)} />
          </Field>
        </div>
        <Field label="Site name">
          <input className="field-input" value={siteName} onChange={(e) => setSiteName(e.target.value)} />
        </Field>
        <Field label="Description">
          <textarea className="field-input" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="grid grid-cols-4 gap-3">
          <Field label="Latitude">
            <input className="field-input" type="number" step="0.000001" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="-4.6796" />
          </Field>
          <Field label="Longitude">
            <input className="field-input" type="number" step="0.000001" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="55.4920" />
          </Field>
          <Field label="Area (ha)">
            <input className="field-input" type="number" step="0.1" value={areaHectares} onChange={(e) => setAreaHectares(e.target.value)} />
          </Field>
          <Field label="District (optional)">
            <input className="field-input" value={district} onChange={(e) => setDistrict(e.target.value)} />
          </Field>
        </div>
        <button
          className="btn-primary w-full"
          disabled={busy || !siteName || !description || !lat || !lng || !areaHectares}
          onClick={submit}
        >
          {busy ? 'Submitting…' : 'Submit application (runs GeoServe + MSP zone check)'}
        </button>
      </div>
    </Modal>
  );
}
